import { Transition } from '@headlessui/react';
import { useEffect, useState, useRef } from 'react';
import { useBjForm } from '@hooks/useBjForm';
import { useTranslation } from 'next-i18next';
import { CardPaymentList } from '@components/features/payment';
import { Input, Button } from '@components/common/Forms';
import { useBillingDataHub } from '@components/features/payment/billing-data/useBillingDataHub';
import { BillingDataSelector } from '@components/templates/company/billing/BillingDataSelector';
import { useExtendedRouter } from '@hooks/next-routing-wrappers';
import { CustomerBillingDataForm } from '@components/templates/company/billing/CustomerBillingDataForm';

/**
 * @description Payment methods interface.
 */
interface PaymentMethodsProps {
  cartDetails?: ApiSchema<'OrderPayment'>,
  orderCredits: <Data, Args>(arg: Args) => Promise<Data | void>,
  savingOrder?: boolean,
  deleteShoppingCart?: () => void,
  stepBack?: () => void,
  transitionProps?: object,
  orderError?: { error: ApiSchema<'ErrorResponse'>; timestamp: number } | null,
}

/**
 * @description Payment methods component.
 * - `cartDetails` - the cart details.
 * - `orderCredits` - function to call when the user clicks the submit button.
 * - `savingOrder` - boolean to show loading state on the submit button.
 * - `deleteShoppingCart` - function to call when the user clicks the delete order button.
 * - `stepBack` - function to call when the user clicks the edit order button.
 * - `transitionProps` - Common transition props for the Transition component. If not provided, there will be no transition.
 */
export const PaymentMethods = (props: PaymentMethodsProps) => {
  // Destructure props
  const {
    cartDetails,
    orderCredits,
    savingOrder,
    deleteShoppingCart,
    stepBack,
    transitionProps,
    orderError,
  } = props;

  // Translation
  const { t } = useTranslation('common');
  const { locale } = useExtendedRouter();

  // Form hook
  const { record, handleCallbackSubmit, handleActionErrors } = useBjForm({
    wizardData: { amount: cartDetails?.order?.amount },
  });

  const handleActionErrorsRef = useRef(handleActionErrors);
  handleActionErrorsRef.current = handleActionErrors;

  useEffect(() => {
    if (orderError) {
      handleActionErrors(orderError.error);
    }
  }, [orderError]);

  const {
    billingType,
    billingData,
    setBillingType,
    selectedBillingInfo,
    selectBillingInfo,
    isLoadingBillingType,
  } = useBillingDataHub(locale, 'company');

  // States for managing the selected payment method
  const [selectedMethod, setSelectedMethod] = useState(cartDetails?.paymentTypes?.[0]);
  const [selectedCard, setSelectedCard] = useState(Number(cartDetails?.creditCards?.find(((card) => card.recurrent))?.id) || 0);

  // Render component
  // ********************************
  return (

    <>
      <BillingDataSelector
        setBillingType={setBillingType}
        billingData={billingData}
        selectedBillingInfo={selectedBillingInfo}
        selectBillingInfo={selectBillingInfo}
        isLoadingBillingType={isLoadingBillingType}
      />

      <form
        noValidate
        onSubmit={(event) => handleCallbackSubmit(event, orderCredits)}
      >

        {/* Form content */}
        <Transition
          as="div"
          className="max-w-full bg-surface-100 p-6"
          {...transitionProps}
        >
          <CustomerBillingDataForm
            customer={selectedBillingInfo}
            isIndividual={billingType === 'individual'}
            locale={locale}
            record={record}
          />
          {/* Form data */}
          <Input
            type="hidden"
            value={selectedCard}
            {...record('creditCardId', { convertEmptyValue: 'toNumber' })}
          />
          <Input
            type="hidden"
            value={selectedMethod?.id}
            {...record('paymentTypeId')}
          />

          {/* Payment methods */}
          <CardPaymentList
            paymentMethods={cartDetails?.paymentTypes}
            creditsAmount={cartDetails?.availableCredits}
            creditCards={cartDetails?.creditCards}
            getSelectedMethod={setSelectedMethod}
            getSelectedCard={setSelectedCard}
            showAvailableCredits={false}
            completedProfile={cartDetails?.customer?.vatNumber !== ''}
          />

          {/* Order title */}
          <div className="my-3.5 flex items-center justify-between">
            <h2 className="text-sm font-bold uppercase text-ink-medium">
              {t('global.label.order.details')}
            </h2>

            {/* Edit order (go back) */}
            <Button
              size="xs"
              rounding="full"
              styling="outline"
              color="ink-medium"
              className="shrink-0 !py-1.5"
              onClick={stepBack}
            >
              {t('global.label.edit-order')}
            </Button>
          </div>

          {/* Order details */}
          <div className="divide-y divide-dashed divide-ink/15 border bg-surface-50 p-4">
            {/* Neuro amount */}
            <div className="flex items-center justify-between py-1">
              <span>{t('neuro.label.simple.amount')}</span>
              <strong className="text-right text-ink">{`${cartDetails?.order?.amount} n`}</strong>
            </div>

            {/* Package price in euros; Free trial data is first in pecking order */}
            <div className="flex items-center justify-between py-1">
              <span>{t('total.with.vat')}</span>
              <strong className="text-right text-xl text-secondary">
                {cartDetails?.order?.totalPrice}
                <span className="ml-1 text-sm">{cartDetails?.order?.currency}</span>
              </strong>
            </div>
          </div>
        </Transition>

        {/* Footer */}
        <div className="flex items-center justify-between border-t px-6 py-4 text-sm">
          {/* Delete shopping cart button */}
          <Button
            rounding="full"
            color="light"
            size="sm"
            styling="outline"
            className="!px-7"
            onClick={deleteShoppingCart}
          >
            {t('order.bt.cancel')}
          </Button>

          {/* Submit button */}
          <Button
            type="submit"
            rounding="full"
            color="secondary"
            size="sm"
            className="!px-7"
            isLoading={savingOrder}
            disabled={!selectedMethod || isLoadingBillingType}
          >
            {t('global.action.order')}
          </Button>
        </div>
      </form>
    </>

  );
};
